export interface TenantOidcClientSetting {
  id: string;
  label: string;
  value: string;
  description?: string;
}

export interface TenantOidcClientSettings {
  title: string;
  description: string;
  callbackLabel: string;
  callbackUrl: string;
  defaultExpanded: boolean;
  settings: TenantOidcClientSetting[];
}

export interface TenantOidcPortalLocation {
  protocol: string;
  hostname: string;
}

export function publicBaseDomainFromPortalHostname(hostname: string): string | null {
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, '');
  const marker = 'myaccount.';
  const markerIndex = normalized.indexOf(marker);
  if (markerIndex < 0) return null;
  const publicBaseDomain = normalized.slice(markerIndex + marker.length);
  return publicBaseDomain && !publicBaseDomain.includes('/') ? publicBaseDomain : null;
}

export function buildTenantOidcClientSettings(
  tenantId: string,
  location: TenantOidcPortalLocation | null,
  providerExists: boolean,
): TenantOidcClientSettings | null {
  const normalizedTenantId = tenantId.trim().toLowerCase();
  if (!normalizedTenantId || !location) return null;
  const publicBaseDomain = publicBaseDomainFromPortalHostname(location.hostname);
  if (!publicBaseDomain) return null;
  const protocol = location.protocol === 'http:' ? 'http:' : 'https:';
  const callbackUrl = `${protocol}//auth.${publicBaseDomain}/realms/api/broker/tenant-${normalizedTenantId}-oidc/endpoint`;

  return {
    title: 'Client settings for your identity provider',
    description: 'Create the upstream OIDC client with these values, then enter its issuer, client ID, and client secret above.',
    callbackLabel: 'Valid redirect URI',
    callbackUrl,
    defaultExpanded: !providerExists,
    settings: [
      { id: 'client-type', label: 'Client type', value: 'OpenID Connect' },
      { id: 'client-authentication', label: 'Client authentication', value: 'On' },
      { id: 'authentication-method', label: 'Token endpoint authentication', value: 'Client ID and Secret (client_secret_post)' },
      { id: 'standard-flow', label: 'Standard flow', value: 'On' },
      { id: 'implicit-flow', label: 'Implicit flow', value: 'Off' },
      { id: 'direct-grants', label: 'Direct access grants', value: 'Off' },
      { id: 'service-accounts', label: 'Service accounts', value: 'Off' },
      { id: 'pkce', label: 'PKCE method', value: 'S256' },
      { id: 'requested-scope', label: 'Requested scope', value: 'openid plus any scopes configured below' },
      { id: 'default-scopes', label: 'Default client scopes', value: 'profile, email' },
      { id: 'required-claims', label: 'Required claims', value: 'sub, email, email_verified (boolean)' },
      { id: 'optional-claims', label: 'Optional claims', value: 'name' },
      { id: 'role-claim', label: 'Role mapping claim', value: 'Optional array of strings at the configured dotted claim path' },
      { id: 'redirect-uri', label: 'Valid redirect URIs', value: callbackUrl, description: 'Use this exact URI. Do not add a wildcard.' },
      { id: 'unused-urls', label: 'Other URLs', value: 'Web origins, root, home, admin, and post-logout URLs are not required' },
    ],
  };
}
